/**
 * In-doc comments + unapproved suggestions prototype (?test=doc-comments)
 *
 * Multi-User Comm bet. Feel: Figma-style badges in the RIGHT gutter (toward
 * the Comments rail) + Google Docs Comments rail.
 * Sample body: real Speedy work-spec prose (varied blocks). Compose + @tags
 * live in the rail. Shell: ?test=maater-ui Proto chrome.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react'
import {
  ArrowUp,
  AtSign,
  Check,
  MessageSquarePlus,
  Sparkles,
  X,
  ChevronDown,
  Highlighter,
} from 'lucide-react'
import {
  ProtoAppHeader,
  ProtoIconRail,
  ProtoTitleRow,
} from '@/components/prototype/maater-proto-chrome'
import { maaterProtoAppSkinStyle } from '@/components/prototype/maater-proto-shell'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'
import { OpenDestinationIcons } from '@/lib/open-destination-icons'
import { useTheme } from '@/providers/theme-provider'
import '@/components/features/document-reading-prose.css'
import {
  SAMPLE_DOC_META,
  SAMPLE_PROSE_BLOCKS,
  type ProseBlock,
} from '@/components/test-doc-comments-sample'

/** Product rule for range marks (TipTap). Empty selection = whole-block comment. */
const MIN_RANGE_CHARS = 1

type SuggestionStatus = 'pending' | 'accepted' | 'rejected'
type ComposeMode = 'comment' | 'suggest'
type RailFilter = 'open' | 'resolved' | 'all'
/** block = only threads for focused element; document = all, grouped by element */
type RailScope = 'block' | 'document'
type MentionKind = 'user' | 'agent'

interface MentionOption {
  id: string
  handle: string
  label: string
  kind: MentionKind
}

interface DiscussionThread {
  id: string
  blockId: string
  /** Optional mid-block phrase (TipTap mark range stand-in) */
  excerpt: string | null
  author: string
  body: string
  mentions: string[]
  resolved: boolean
  kind: 'discussion'
}

interface Suggestion {
  id: string
  blockId: string
  excerpt: string | null
  original: string
  proposed: string
  status: SuggestionStatus
  note: string
  mentions: string[]
  kind: 'suggestion'
}

type ThreadItem = DiscussionThread | Suggestion

interface ComposerDraft {
  blockId: string
  excerpt: string | null
  text: string
  mode: ComposeMode
  mentions: string[]
}

const MENTION_DIRECTORY: MentionOption[] = [
  { id: 'dan', handle: '@dan', label: 'Dan Nelson', kind: 'user' },
  { id: 'tj', handle: '@tj', label: 'TJ Bracegirdle', kind: 'user' },
  { id: 'agent', handle: '@agent', label: 'Default agent', kind: 'agent' },
  { id: 'research', handle: '@research', label: 'Research agent', kind: 'agent' },
]

const SEED_THREADS: DiscussionThread[] = [
  {
    id: 't1',
    blockId: 'p-overview-2',
    excerpt: 'load the banner via a small JS fetch from an uncached EE endpoint',
    author: 'RS',
    body: 'This is the load-bearing decision - call it out in the Basecamp estimate so RDEK knows why alerts are special.',
    mentions: ['@tj'],
    resolved: false,
    kind: 'discussion',
  },
  {
    id: 't2',
    blockId: 'tbl-decisions',
    excerpt: 'Granular per channel. Widget Elements = full static clear',
    author: 'TJ',
    body: 'This cell is the important one - full clear on widget save matches Louv lessons.',
    mentions: [],
    resolved: false,
    kind: 'discussion',
  },
  {
    id: 't2b',
    blockId: 'tbl-decisions',
    excerpt: 'JS-loaded from uncached alerts/banner - not baked into static HTML',
    author: 'RS',
    body: 'Call this out in the estimate - alerts are the exception path.',
    mentions: ['@tj'],
    resolved: false,
    kind: 'discussion',
  },
  {
    id: 't6',
    blockId: 'list-smoke',
    excerpt:
      'Open/close an alert - banner updates sitewide via JS without wiping the static tree; /current_advisories/ rebuilds; dismiss still works.',
    author: 'TJ',
    body: 'This smoke item is the one that usually fails first - keep it explicit in QA.',
    mentions: [],
    resolved: false,
    kind: 'discussion',
  },
  {
    id: 't3',
    blockId: 'code-config',
    excerpt: null,
    author: 'RS',
    body: 'Confirm speedy_ttl = 0 matches Louv staging before we quote hours.',
    mentions: ['@tj'],
    resolved: false,
    kind: 'discussion',
  },
  {
    id: 't4',
    blockId: 'code-config',
    excerpt: "$config['speedy_logged_out_only'] = 'yes';",
    author: 'TJ',
    body: 'Logged-out-only is non-negotiable - editors must not poison public static files.',
    mentions: [],
    resolved: false,
    kind: 'discussion',
  },
  {
    id: 't5',
    blockId: 'code-config',
    excerpt: null,
    author: 'You',
    body: 'Add a one-line comment in config.php pointing ops to ignore URL list.',
    mentions: ['@agent'],
    resolved: false,
    kind: 'discussion',
  },
]

const SEED_SUGGESTIONS: Suggestion[] = [
  {
    id: 's1',
    blockId: 'p-cf',
    excerpt: 'Speedy clear is not the same as CF purge.',
    original: 'Speedy clear is not the same as CF purge.',
    proposed:
      'Speedy clear does not purge Cloudflare. Treat Speedy and CF as two cache layers that must both be aligned.',
    status: 'pending',
    note: '@agent tighten opening sentence for client estimate',
    mentions: ['@agent'],
    kind: 'suggestion',
  },
]

const ids = testIds.docCommentsPrototype

function blockPlainText(block: ProseBlock): string {
  return block.text
}

function blockPreviewLabel(block: ProseBlock | undefined): string {
  if (!block) return 'Unknown block'
  const oneLine = block.text.replace(/\n/g, ' ').trim()
  const clip = oneLine.length > 72 ? `${oneLine.slice(0, 72)}...` : oneLine
  if (block.kind === 'heading') return clip
  if (block.kind === 'code') return 'PHP / Speedy config'
  if (block.kind === 'table') return clip.split('|')[0] ? `Table: ${clip.slice(0, 48)}` : 'Table'
  return clip
}

function applyTextReplace(
  blocks: ProseBlock[],
  blockId: string,
  original: string,
  proposed: string,
): ProseBlock[] {
  return blocks.map((b) => {
    if (b.id !== blockId) return b
    if (b.text.includes(original)) {
      return { ...b, text: b.text.replace(original, proposed) }
    }
    return { ...b, text: proposed }
  })
}

type TextMark = { excerpt: string; active: boolean }

type InlineRevision = {
  id: string
  original: string
  proposed: string
  /** When true, show del/ins (+/-) and optional Accept/Reject in the doc */
  showDiff: boolean
}

/**
 * Paint range marks + optional active suggestion revision (strikethrough /
 * addition) inline. Not a TipTap Pro diff - just a readable prototype.
 */
function renderWithMarks(
  text: string,
  marks: TextMark[],
  revision: InlineRevision | null,
  onAccept?: (id: string) => void,
  onReject?: (id: string) => void,
): ReactNode {
  // Active suggestion: replace original span with inline -/+ revision.
  if (
    revision?.showDiff &&
    revision.original.length >= MIN_RANGE_CHARS &&
    text.includes(revision.original)
  ) {
    const i = text.indexOf(revision.original)
    const before = text.slice(0, i)
    const after = text.slice(i + revision.original.length)
    // Other marks only on the surrounding text (skip the revised span).
    const sideMarks = marks.filter(
      (m) =>
        m.excerpt !== revision.original &&
        !revision.original.includes(m.excerpt),
    )
    return (
      <>
        {renderWithMarks(before, sideMarks, null)}
        <span
          className="inline align-baseline [box-decoration-break:clone]"
          data-testid={`doc-comments-inline-rev-${revision.id}`}
        >
          <del className="rounded-none bg-destructive/15 px-0.5 text-muted-foreground decoration-destructive/70">
            {revision.original}
          </del>{' '}
          <ins className="rounded-none bg-emerald-500/20 px-0.5 text-foreground no-underline">
            {revision.proposed}
          </ins>
          {onAccept && onReject ? (
            <span className="ml-1.5 inline-flex translate-y-[-1px] items-center gap-0.5 align-middle">
              <button
                type="button"
                className="inline-flex h-5 items-center gap-0.5 rounded-none bg-primary px-1.5 text-[10px] font-medium text-primary-foreground"
                onClick={(e) => {
                  e.stopPropagation()
                  onAccept(revision.id)
                }}
              >
                <Check className="h-3 w-3" /> Accept
              </button>
              <button
                type="button"
                className="inline-flex h-5 items-center gap-0.5 rounded-none border border-border bg-background px-1.5 text-[10px] text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation()
                  onReject(revision.id)
                }}
              >
                <X className="h-3 w-3" /> Reject
              </button>
            </span>
          ) : null}
        </span>
        {renderWithMarks(after, sideMarks, null)}
      </>
    )
  }

  const usable = marks.filter(
    (m) => m.excerpt.length >= MIN_RANGE_CHARS && text.includes(m.excerpt),
  )
  if (usable.length === 0) return text

  // Prefer longer excerpts first so nested phrases still wrap correctly.
  const sorted = [...usable].sort((a, b) => b.excerpt.length - a.excerpt.length)
  type Seg = { start: number; end: number; active: boolean }
  const segs: Seg[] = []
  for (const m of sorted) {
    const start = text.indexOf(m.excerpt)
    if (start < 0) continue
    const end = start + m.excerpt.length
    const overlaps = segs.some((s) => start < s.end && end > s.start)
    if (overlaps) continue
    segs.push({ start, end, active: m.active })
  }
  segs.sort((a, b) => a.start - b.start)

  const nodes: ReactNode[] = []
  let cursor = 0
  segs.forEach((seg, idx) => {
    if (cursor < seg.start) nodes.push(text.slice(cursor, seg.start))
    nodes.push(
      <mark
        key={`m-${idx}-${seg.start}`}
        className={cn(
          'rounded-none px-0.5 text-inherit [box-decoration-break:clone]',
          seg.active ? 'bg-primary/30' : 'bg-primary/15',
        )}
      >
        {text.slice(seg.start, seg.end)}
      </mark>,
    )
    cursor = seg.end
  })
  if (cursor < text.length) nodes.push(text.slice(cursor))
  return <>{nodes}</>
}

function parseTable(text: string): { headers: string[]; rows: string[][] } {
  const lines = text.split('\n').filter(Boolean)
  const headers = (lines[0] ?? '').split('|').map((c) => c.trim())
  const rows = lines.slice(1).map((line) => line.split('|').map((c) => c.trim()))
  return { headers, rows }
}

export function DocCommentsPrototype() {
  const { theme, setTheme } = useTheme()
  const [blocks, setBlocks] = useState<ProseBlock[]>(SAMPLE_PROSE_BLOCKS)
  const [discussions, setDiscussions] = useState<DiscussionThread[]>(SEED_THREADS)
  const [suggestions, setSuggestions] = useState<Suggestion[]>(SEED_SUGGESTIONS)
  const [activeBlockId, setActiveBlockId] = useState<string | null>('p-overview-2')
  const [activeThreadId, setActiveThreadId] = useState<string | null>('t1')
  const [activeExcerpt, setActiveExcerpt] = useState<string | null>(
    SEED_THREADS[0]?.excerpt ?? null,
  )
  const [railOpen, setRailOpen] = useState(true)
  const [agentOpen, setAgentOpen] = useState(false)
  const [railFilter, setRailFilter] = useState<RailFilter>('open')
  const [railScope, setRailScope] = useState<RailScope>('block')
  const [hoveredBlockId, setHoveredBlockId] = useState<string | null>(null)
  const [draft, setDraft] = useState<ComposerDraft | null>({
    blockId: 'p-overview-2',
    excerpt: SEED_THREADS[0]?.excerpt ?? null,
    text: '',
    mode: 'comment',
    mentions: [],
  })
  const [mentionQuery, setMentionQuery] = useState<string | null>(null)
  const [switchPrompt, setSwitchPrompt] = useState<string | null>(null)
  const [orchLog, setOrchLog] = useState<string[]>([
    'Agent chat (separate surface). Doc in context: Speedy caching work spec (RDEK).',
    'Escalate from Comments rail, or ask to review the editing pass.',
  ])
  const composerRef = useRef<HTMLTextAreaElement>(null)
  const blockRefs = useRef<Record<string, HTMLElement | null>>({})

  const pending = useMemo(
    () => suggestions.filter((s) => s.status === 'pending'),
    [suggestions],
  )

  const openThreads = useMemo(() => {
    const items: ThreadItem[] = [
      ...discussions.filter((d) => !d.resolved),
      ...suggestions.filter((s) => s.status === 'pending'),
    ]
    return items
  }, [discussions, suggestions])

  const statusFilteredItems = useMemo(() => {
    const all: ThreadItem[] = [...discussions, ...suggestions]
    if (railFilter === 'all') return all
    if (railFilter === 'resolved') {
      return all.filter(
        (t) =>
          (t.kind === 'discussion' && t.resolved) ||
          (t.kind === 'suggestion' && t.status !== 'pending'),
      )
    }
    return openThreads
  }, [discussions, suggestions, railFilter, openThreads])

  /** When scoped to a block, only that element's threads (matches gutter badge). */
  const filteredRailItems = useMemo(() => {
    if (railScope === 'block' && activeBlockId) {
      return statusFilteredItems.filter((t) => t.blockId === activeBlockId)
    }
    return statusFilteredItems
  }, [statusFilteredItems, railScope, activeBlockId])

  /** Document view: threads grouped in document order. */
  const groupedRailSections = useMemo(() => {
    if (railScope !== 'document') return []
    const byBlock = new Map<string, ThreadItem[]>()
    for (const item of statusFilteredItems) {
      const list = byBlock.get(item.blockId) ?? []
      list.push(item)
      byBlock.set(item.blockId, list)
    }
    return blocks
      .filter((b) => byBlock.has(b.id))
      .map((b) => ({
        block: b,
        items: byBlock.get(b.id) ?? [],
      }))
  }, [railScope, statusFilteredItems, blocks])

  const focusedBlockThreadCount = useMemo(() => {
    if (!activeBlockId) return 0
    return openThreads.filter((t) => t.blockId === activeBlockId).length
  }, [activeBlockId, openThreads])

  const mentionSuggestions = useMemo(() => {
    if (mentionQuery === null) return []
    const q = mentionQuery.toLowerCase()
    return MENTION_DIRECTORY.filter(
      (m) =>
        m.handle.toLowerCase().includes(q) ||
        m.label.toLowerCase().includes(q),
    )
  }, [mentionQuery])

  const scrollBlockIntoView = (blockId: string) => {
    const el = blockRefs.current[blockId]
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const focusThread = useCallback((item: ThreadItem) => {
    setActiveBlockId(item.blockId)
    setActiveThreadId(item.id)
    setActiveExcerpt(item.excerpt)
    setRailOpen(true)
    setRailScope('block')
    setDraft((prev) =>
      prev?.blockId === item.blockId
        ? { ...prev, excerpt: item.excerpt }
        : {
            blockId: item.blockId,
            excerpt: item.excerpt,
            text: '',
            mode: 'comment',
            mentions: [],
          },
    )
    requestAnimationFrame(() => scrollBlockIntoView(item.blockId))
  }, [])

  const openComposeOnBlock = useCallback(
    (blockId: string, excerpt: string | null = null) => {
      if (activeBlockId && activeBlockId !== blockId && draft?.text.trim()) {
        setSwitchPrompt(blockId)
        return
      }
      setSwitchPrompt(null)
      setActiveBlockId(blockId)
      setActiveThreadId(null)
      setActiveExcerpt(excerpt)
      setRailOpen(true)
      setRailScope('block')
      setDraft((prev) =>
        prev?.blockId === blockId
          ? { ...prev, excerpt }
          : { blockId, excerpt, text: '', mode: 'comment', mentions: [] },
      )
      requestAnimationFrame(() => composerRef.current?.focus())
    },
    [activeBlockId, draft],
  )

  const focusBlockFromBadge = useCallback(
    (blockId: string) => {
      setRailOpen(true)
      setRailScope('block')
      const first =
        discussions.find((d) => d.blockId === blockId && !d.resolved) ??
        suggestions.find((s) => s.blockId === blockId && s.status === 'pending')
      if (first) {
        focusThread(first)
        return
      }
      openComposeOnBlock(blockId)
    },
    [discussions, suggestions, focusThread, openComposeOnBlock],
  )

  const confirmSwitch = (save: boolean) => {
    if (!switchPrompt) return
    if (!save) {
      setDraft({
        blockId: switchPrompt,
        excerpt: null,
        text: '',
        mode: 'comment',
        mentions: [],
      })
    } else if (draft) {
      setDraft({ ...draft, blockId: switchPrompt, excerpt: null })
    }
    setActiveBlockId(switchPrompt)
    setActiveThreadId(null)
    setActiveExcerpt(null)
    setRailOpen(true)
    setRailScope('block')
    setSwitchPrompt(null)
  }

  const addMention = (m: MentionOption) => {
    if (!draft) return
    const nextMentions = draft.mentions.includes(m.handle)
      ? draft.mentions
      : [...draft.mentions, m.handle]
    let nextText = draft.text
    if (mentionQuery !== null) {
      nextText = nextText.replace(/@[\w.-]*$/, '') + m.handle + ' '
    } else if (!nextText.includes(m.handle)) {
      nextText = (nextText.trim() ? nextText.trimEnd() + ' ' : '') + m.handle + ' '
    }
    setDraft({ ...draft, text: nextText, mentions: nextMentions })
    setMentionQuery(null)
    requestAnimationFrame(() => composerRef.current?.focus())
  }

  const onComposerChange = (value: string) => {
    if (!draft) return
    setDraft({ ...draft, text: value })
    const at = value.match(/@([\w.-]*)$/)
    setMentionQuery(at ? at[1] : null)
  }

  const submitCompose = () => {
    if (!draft || !activeBlockId || !draft.text.trim()) return
    const block = blocks.find((b) => b.id === activeBlockId)
    if (!block) return

    const fromText = MENTION_DIRECTORY.filter((m) =>
      draft.text.toLowerCase().includes(m.handle.toLowerCase()),
    ).map((m) => m.handle)
    const mentions = Array.from(new Set([...draft.mentions, ...fromText]))

    if (draft.mode === 'suggest') {
      const original = draft.excerpt ?? blockPlainText(block)
      const proposed =
        draft.text.replace(/@[\w.-]+/g, '').trim().length > 20
          ? draft.text.replace(/@[\w.-]+/g, '').trim()
          : `${original} (${draft.text.trim()})`
      const sug: Suggestion = {
        id: `s-${Date.now()}`,
        blockId: activeBlockId,
        excerpt: draft.excerpt,
        original,
        proposed,
        status: 'pending',
        note: draft.text.trim().slice(0, 100),
        mentions,
        kind: 'suggestion',
      }
      setSuggestions((prev) => [...prev, sug])
      setActiveThreadId(sug.id)
      setOrchLog((prev) => [
        ...prev,
        `Suggestion on ${activeBlockId}${draft.excerpt ? ' (range)' : ''}: ${mentions.join(' ') || '(no tags)'}`,
      ])
    } else {
      const thread: DiscussionThread = {
        id: `t-${Date.now()}`,
        blockId: activeBlockId,
        excerpt: draft.excerpt,
        author: 'You',
        body: draft.text.trim(),
        mentions,
        resolved: false,
        kind: 'discussion',
      }
      setDiscussions((prev) => [...prev, thread])
      setActiveThreadId(thread.id)
      setOrchLog((prev) => [
        ...prev,
        `Comment on ${activeBlockId}: ${mentions.join(' ') || '(no tags)'} - "${thread.body.slice(0, 50)}"`,
      ])
    }
    setDraft({
      blockId: activeBlockId,
      excerpt: draft.excerpt,
      text: '',
      mode: draft.mode,
      mentions: [],
    })
    setMentionQuery(null)
  }

  const acceptSuggestion = (id: string) => {
    const s = suggestions.find((x) => x.id === id)
    if (!s) return
    setSuggestions((prev) =>
      prev.map((x) => (x.id === id ? { ...x, status: 'accepted' as const } : x)),
    )
    setBlocks((prev) => applyTextReplace(prev, s.blockId, s.original, s.proposed))
    setOrchLog((prev) => [...prev, `Accepted suggestion ${id} - body updated.`])
  }

  const rejectSuggestion = (id: string) => {
    setSuggestions((prev) =>
      prev.map((s) => (s.id === id ? { ...s, status: 'rejected' as const } : s)),
    )
    setOrchLog((prev) => [...prev, `Rejected suggestion ${id}.`])
  }

  const resolveDiscussion = (id: string) => {
    setDiscussions((prev) =>
      prev.map((d) => (d.id === id ? { ...d, resolved: true } : d)),
    )
  }

  const runOrchestratorReview = () => {
    setAgentOpen(true)
    if (pending.length === 0) {
      setOrchLog((prev) => [...prev, 'No pending suggestions in this editing pass.'])
      return
    }
    setOrchLog((prev) => [
      ...prev,
      `Review: ${pending.length} pending. Recommend accepting CF sentence rewrite; then ask @tj to confirm alert JS wording.`,
    ])
  }

  const escalateToChat = () => {
    setAgentOpen(true)
    const label = activeThreadId ?? activeBlockId ?? 'doc'
    setOrchLog((prev) => [
      ...prev,
      `Escalated from Comments rail (${label}). Seed: Speedy doc + open threads + tags.`,
    ])
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && draft?.text.trim()) {
        e.preventDefault()
        submitCompose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- prototype submit
  }, [draft])

  const ChatIcon = OpenDestinationIcons.openInChat

  const badgeCountForBlock = (blockId: string) => {
    const d = discussions.filter((t) => t.blockId === blockId && !t.resolved).length
    const s = suggestions.filter((t) => t.blockId === blockId && t.status === 'pending')
      .length
    return d + s
  }

  /** Idle marks for every open thread with an excerpt; active thread stronger. */
  const marksForBlock = (blockId: string): TextMark[] => {
    const fromDiscussions = discussions
      .filter((d) => d.blockId === blockId && !d.resolved && d.excerpt)
      .map((d) => ({
        excerpt: d.excerpt as string,
        active: d.id === activeThreadId,
      }))
    // Pending suggestions: mark original when not showing active inline diff.
    const fromSuggestions = suggestions
      .filter((s) => s.blockId === blockId && s.status === 'pending' && s.excerpt)
      .filter((s) => s.id !== activeThreadId)
      .map((s) => ({
        excerpt: (s.excerpt ?? s.original) as string,
        active: false,
      }))
    return [...fromDiscussions, ...fromSuggestions]
  }

  /** Active pending suggestion on this block -> inline +/- when selected. */
  const revisionForBlock = (blockId: string): InlineRevision | null => {
    const s = suggestions.find(
      (x) =>
        x.blockId === blockId &&
        x.status === 'pending' &&
        x.id === activeThreadId,
    )
    if (!s) return null
    return {
      id: s.id,
      original: s.original,
      proposed: s.proposed,
      showDiff: true,
    }
  }

  const renderThreadCard = (item: ThreadItem) => {
    const focused = activeThreadId === item.id
    const block = blocks.find((b) => b.id === item.blockId)
    return (
      <div
        key={item.id}
        role="button"
        tabIndex={0}
        onClick={() => focusThread(item)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') focusThread(item)
        }}
        className={cn(
          'rounded-none border px-3 py-2 text-xs transition-colors',
          focused
            ? 'border-primary bg-primary/5'
            : 'border-border bg-background hover:bg-muted/40',
          item.kind === 'suggestion' &&
            item.status === 'pending' &&
            'border-dashed',
        )}
        data-testid={ids.railCard(item.id)}
      >
        {railScope === 'document' && (
          <div className="mb-1 flex flex-wrap items-center gap-1">
            <span className="rounded-none bg-muted px-1.5 py-0.5 text-[10px] font-medium capitalize text-muted-foreground">
              {block?.kind ?? 'block'}
            </span>
          </div>
        )}
        {item.excerpt && (
          <div className="mb-1.5 inline-flex max-w-full items-center gap-0.5 truncate text-[10px] text-primary">
            <Highlighter className="h-3 w-3 shrink-0" />
            <span className="truncate">{item.excerpt}</span>
          </div>
        )}

        {item.kind === 'suggestion' ? (
          <>
            <div className="mb-1 flex items-center gap-1 font-medium text-primary">
              <Sparkles className="h-3.5 w-3.5" />
              {item.status === 'pending' ? 'Unapproved suggestion' : item.status}
            </div>
            <p className="mb-1 text-[10px] text-muted-foreground">{item.note}</p>
            {item.mentions.length > 0 && (
              <div className="mb-1.5 flex flex-wrap gap-1">
                {item.mentions.map((m) => (
                  <span
                    key={m}
                    className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary"
                  >
                    {m}
                  </span>
                ))}
              </div>
            )}
            {item.status === 'pending' && (
              <>
                <p className="mb-1 line-through opacity-60">{item.original}</p>
                <p className="mb-2">{item.proposed}</p>
                <div className="flex gap-1">
                  <Button
                    type="button"
                    size="sm"
                    className="h-7 gap-1"
                    onClick={(e) => {
                      e.stopPropagation()
                      acceptSuggestion(item.id)
                    }}
                    data-testid={ids.acceptSuggestion(item.id)}
                  >
                    <Check className="h-3.5 w-3.5" /> Accept
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 gap-1"
                    onClick={(e) => {
                      e.stopPropagation()
                      rejectSuggestion(item.id)
                    }}
                  >
                    <X className="h-3.5 w-3.5" /> Reject
                  </Button>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <div className="mb-1 flex items-center justify-between gap-2">
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[10px] font-semibold">
                {item.author.slice(0, 2)}
              </span>
              <span className="text-[10px] text-muted-foreground">
                {item.resolved ? 'Resolved' : 'Open'}
              </span>
            </div>
            {item.mentions.length > 0 && (
              <div className="mb-1.5 flex flex-wrap gap-1">
                {item.mentions.map((m) => (
                  <span
                    key={m}
                    className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary"
                  >
                    {m}
                  </span>
                ))}
              </div>
            )}
            <p className="mb-2 leading-relaxed">{item.body}</p>
            {!item.resolved && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-6 text-[10px]"
                onClick={(e) => {
                  e.stopPropagation()
                  resolveDiscussion(item.id)
                }}
              >
                Resolve
              </Button>
            )}
          </>
        )}
      </div>
    )
  }

  const renderBlock = (block: ProseBlock) => {
    const count = badgeCountForBlock(block.id)
    const isFocused = activeBlockId === block.id
    const isHovered = hoveredBlockId === block.id && !isFocused
    const marks = marksForBlock(block.id)
    const revision = revisionForBlock(block.id)
    const paint = (text: string) =>
      renderWithMarks(
        text,
        marks,
        revision && text.includes(revision.original) ? revision : null,
        acceptSuggestion,
        rejectSuggestion,
      )

    const content = (() => {
      switch (block.kind) {
        case 'heading': {
          const Tag = (`h${block.level ?? 2}` as 'h1' | 'h2' | 'h3')
          return <Tag>{paint(block.text)}</Tag>
        }
        case 'paragraph':
          return <p className="leading-relaxed">{paint(block.text)}</p>
        case 'list': {
          // Same unit pattern as table cells: one li = one selectable anchor.
          const unitActive = (text: string) =>
            activeBlockId === block.id &&
            !!activeExcerpt &&
            activeExcerpt === text
          const unitHasThread = (text: string) =>
            marks.some((m) => m.excerpt === text)
          const onListItem = (e: MouseEvent, text: string) => {
            e.stopPropagation()
            openComposeOnBlock(block.id, text.trim() || null)
          }
          const ListTag = block.ordered ? 'ol' : 'ul'
          return (
            <ListTag className={block.ordered ? 'list-decimal' : 'list-disc'}>
              {block.text.split('\n').map((item, i) => (
                <li
                  key={i}
                  data-testid={ids.listItem(block.id, i)}
                  title="Comment on this list item"
                  onClick={(e) => onListItem(e, item)}
                  className={cn(
                    'cursor-pointer rounded-none px-1 py-0.5 transition-colors',
                    unitActive(item) &&
                      'bg-primary/15 outline outline-1 outline-primary/50 outline-offset-[-1px]',
                    !unitActive(item) && unitHasThread(item) && 'bg-primary/10',
                    !unitActive(item) && 'hover:bg-muted/60',
                  )}
                >
                  {paint(item)}
                </li>
              ))}
            </ListTag>
          )
        }
        case 'code':
          return (
            <pre className="overflow-x-auto rounded-none border border-border bg-muted/40 p-3 text-[13px] leading-relaxed">
              <code>{paint(block.text)}</code>
            </pre>
          )
        case 'table': {
          const { headers, rows } = parseTable(block.text)
          const cellActive = (text: string) =>
            activeBlockId === block.id &&
            !!activeExcerpt &&
            activeExcerpt === text
          const cellHasThread = (text: string) =>
            marks.some((m) => m.excerpt === text)
          const onCell = (e: MouseEvent, text: string) => {
            e.stopPropagation()
            const excerpt = text.trim() || null
            openComposeOnBlock(block.id, excerpt)
          }
          return (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>
                    {headers.map((h, ci) => (
                      <th
                        key={`h-${ci}`}
                        data-testid={ids.tableCell(block.id, -1, ci)}
                        title="Comment on this header cell"
                        onClick={(e) => onCell(e, h)}
                        className={cn(
                          'cursor-pointer border border-border bg-muted/40 px-2 py-1.5 text-left font-semibold transition-colors',
                          cellActive(h) &&
                            'bg-primary/15 outline outline-1 outline-primary/50 outline-offset-[-1px]',
                          !cellActive(h) &&
                            cellHasThread(h) &&
                            'bg-primary/10',
                          !cellActive(h) && 'hover:bg-primary/10',
                        )}
                      >
                        {paint(h)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, ri) => (
                    <tr key={ri}>
                      {row.map((cell, ci) => (
                        <td
                          key={ci}
                          data-testid={ids.tableCell(block.id, ri, ci)}
                          title="Comment on this cell"
                          onClick={(e) => onCell(e, cell)}
                          className={cn(
                            'cursor-pointer border border-border px-2 py-1.5 align-top transition-colors',
                            cellActive(cell) &&
                              'bg-primary/15 outline outline-1 outline-primary/50 outline-offset-[-1px]',
                            !cellActive(cell) &&
                              cellHasThread(cell) &&
                              'bg-primary/10',
                            !cellActive(cell) && 'hover:bg-muted/60',
                          )}
                        >
                          {paint(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
        case 'hr':
          return <hr className="my-4 border-border" />
        default:
          return <p>{block.text}</p>
      }
    })()

    return (
      <div
        key={block.id}
        ref={(el) => {
          blockRefs.current[block.id] = el
        }}
        className="group relative"
        data-testid={ids.block(block.id)}
        data-block-kind={block.kind}
        onMouseEnter={() => setHoveredBlockId(block.id)}
        onMouseLeave={() =>
          setHoveredBlockId((prev) => (prev === block.id ? null : prev))
        }
      >
        {count > 0 ? (
          <button
            type="button"
            className={cn(
              'absolute -right-11 top-1 z-10 flex h-7 min-w-7 items-center justify-center rounded-full border px-1.5 text-[11px] font-semibold shadow-sm transition-colors',
              isFocused
                ? 'border-primary bg-primary text-primary-foreground'
                : isHovered
                  ? 'border-primary/50 bg-primary/10 text-foreground'
                  : 'border-border bg-card text-foreground hover:border-primary/60',
            )}
            data-testid={ids.gutterBadge(block.id)}
            aria-label={`${count} conversations on this block`}
            onClick={() => focusBlockFromBadge(block.id)}
          >
            {count === 1 ? (
              <span className="text-[10px]">
                {discussions.find((d) => d.blockId === block.id)?.author.slice(0, 2) ??
                  'AI'}
              </span>
            ) : (
              count
            )}
          </button>
        ) : (
          <button
            type="button"
            className={cn(
              'absolute -right-11 top-1 z-10 flex h-7 w-7 items-center justify-center rounded-full border border-dashed text-muted-foreground transition-all',
              isFocused || isHovered
                ? 'opacity-100 border-border bg-card'
                : 'opacity-0 group-hover:opacity-100',
            )}
            data-testid={ids.addComment(block.id)}
            aria-label="Add comment on block"
            onClick={() => openComposeOnBlock(block.id)}
          >
            <MessageSquarePlus className="h-3.5 w-3.5" />
          </button>
        )}

        <div
          role="button"
          tabIndex={0}
          onClick={() => openComposeOnBlock(block.id, null)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') openComposeOnBlock(block.id, null)
          }}
          className={cn(
            'rounded-none px-2 py-1.5 transition-colors',
            isFocused &&
              'bg-primary/5 outline outline-1 outline-primary/40 outline-offset-3',
            isHovered &&
              'bg-muted/45 outline outline-1 outline-border outline-offset-3',
            !isFocused &&
              !isHovered &&
              count > 0 &&
              'outline outline-1 outline-primary/15 outline-offset-3',
            block.kind === 'heading' && 'cursor-default',
          )}
        >
          {content}
        </div>
      </div>
    )
  }

  const activeBlock = blocks.find((b) => b.id === activeBlockId)

  return (
    <div
      className="flex h-screen min-h-screen flex-col bg-background text-foreground antialiased"
      style={maaterProtoAppSkinStyle()}
      data-maater-gallery-skin="app"
      data-testid={ids.root}
    >
      <ProtoAppHeader
        themeControl="light-dark"
        appTheme={theme}
        onAppTheme={(t) => {
          void setTheme(t)
        }}
        badge="doc-comments"
        testId={ids.appHeader}
        prototypeIndexTestId={ids.prototypeIndex}
      />

      <div className="flex min-h-0 flex-1">
        <ProtoIconRail active="documents" testId={ids.iconRail} />

        <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <ProtoTitleRow
            crumbs={[
              { id: 'docs', label: 'Documents' },
              { id: 'rdek', label: 'RDEK' },
              { id: 'spec', label: 'Speedy caching' },
            ]}
            title={SAMPLE_DOC_META.title}
            subtitle={`${pending.length} pending · ${openThreads.length} open · ${blocks.length} TipTap-like blocks`}
            testId={ids.titleRow}
            actions={
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-8 gap-1.5"
                  onClick={() => setRailOpen((v) => !v)}
                  data-testid={ids.railToggle}
                >
                  <MessageSquarePlus
                    className={cn('h-4 w-4', railOpen && 'text-primary')}
                  />
                  Comments
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-8 gap-1.5"
                  onClick={escalateToChat}
                  data-testid={ids.openInChat}
                >
                  <ChatIcon className="h-4 w-4" />
                  Open in chat
                </Button>
              </>
            }
          />

          <div className="flex min-h-0 flex-1 overflow-hidden">
            <div
              className="relative min-w-0 flex-1 overflow-y-auto px-6 py-5 md:px-12"
              data-testid={ids.docPane}
            >
              <div className="mb-5 max-w-2xl rounded-none border border-border bg-muted/30 px-3 py-2 text-[11px] leading-relaxed text-muted-foreground">
                <p className="mb-1 font-medium text-foreground">Highlighting</p>
                <ul className="list-disc space-y-0.5 pl-4">
                  <li>
                    <strong>Block</strong> - hover / focus square outline + light fill;
                    commented blocks keep a faint outline at rest.
                  </li>
                  <li>
                    <strong>Range / unit</strong> - table cell or list item click
                    anchors that unit. Active suggestion shows inline -/+
                    (strikethrough + addition) with Accept/Reject in the doc.
                    Click block chrome = whole-block comment.
                  </li>
                  <li>
                    <strong>Min range</strong> - {MIN_RANGE_CHARS}+ chars; 0 selected =
                    whole block.
                  </li>
                </ul>
                <p className="mt-1.5 truncate font-mono text-[10px] opacity-80">
                  Sample: {SAMPLE_DOC_META.sourcePath}
                </p>
              </div>

              <div className="document-reading-prose relative mx-auto max-w-2xl space-y-3 pr-12">
                {blocks.map((block) => renderBlock(block))}
              </div>

              {switchPrompt && (
                <div
                  className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
                  data-testid={ids.switchDialog}
                >
                  <div className="w-full max-w-sm rounded-lg border border-border bg-card p-4 shadow-xl">
                    <p className="mb-3 text-sm font-medium">One active compose thread</p>
                    <p className="mb-4 text-xs text-muted-foreground">
                      Unsaved draft in the Comments rail. Keep the text on the new
                      selection, or discard?
                    </p>
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => confirmSwitch(false)}
                      >
                        Discard
                      </Button>
                      <Button type="button" size="sm" onClick={() => confirmSwitch(true)}>
                        Keep draft text
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {railOpen && (
              <aside
                className="flex w-[360px] shrink-0 flex-col border-l border-border bg-card"
                data-testid={ids.commentsRail}
              >
                {/* Title + drill-down breadcrumb (All > block), not a Block/Doc toggle */}
                <div className="flex h-11 shrink-0 items-center gap-2 border-b border-border px-3">
                  <MessageSquarePlus className="h-4 w-4 shrink-0 text-primary" />
                  <span className="shrink-0 text-sm font-semibold">Comments</span>
                  <nav
                    aria-label="Comments scope"
                    className="flex min-w-0 flex-1 items-center gap-1 text-[11px]"
                    data-testid={ids.railScope}
                  >
                    {railScope === 'block' && activeBlockId ? (
                      <>
                        <button
                          type="button"
                          onClick={() => setRailScope('document')}
                          className="shrink-0 text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                        >
                          All
                          <span className="tabular-nums">
                            {' '}
                            ({openThreads.length})
                          </span>
                        </button>
                        <span className="shrink-0 text-muted-foreground" aria-hidden>
                          /
                        </span>
                        <span className="min-w-0 truncate font-medium capitalize text-foreground">
                          {activeBlock?.kind ?? 'block'}
                          {focusedBlockThreadCount > 0
                            ? ` (${focusedBlockThreadCount})`
                            : ''}
                        </span>
                      </>
                    ) : (
                      <span className="text-muted-foreground">
                        All
                        <span className="tabular-nums"> ({openThreads.length})</span>
                      </span>
                    )}
                  </nav>
                  <select
                    aria-label="Comment status filter"
                    data-testid={ids.railFilter}
                    className="h-7 max-w-[88px] shrink-0 rounded-none border border-border bg-background px-1 text-[11px] text-foreground"
                    value={railFilter}
                    onChange={(e) => setRailFilter(e.target.value as RailFilter)}
                  >
                    <option value="open">Open</option>
                    <option value="resolved">Resolved</option>
                    <option value="all">All</option>
                  </select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={() => setRailOpen(false)}
                    aria-label="Close comments rail"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-2">
                  {railScope === 'block' && !activeBlockId && (
                    <p className="px-2 py-8 text-center text-xs text-muted-foreground">
                      Select something in the doc to comment.
                    </p>
                  )}
                  {railScope === 'block' && activeBlockId && filteredRailItems.length === 0 && (
                    <p
                      className="px-2 py-8 text-center text-xs text-muted-foreground"
                      data-testid={ids.railContext}
                    >
                      No {railFilter === 'open' ? 'open ' : ''}comments here yet.
                    </p>
                  )}
                  {railScope === 'block' &&
                    filteredRailItems.map((item) => renderThreadCard(item))}

                  {railScope === 'document' && groupedRailSections.length === 0 && (
                    <p className="px-2 py-8 text-center text-xs text-muted-foreground">
                      No threads in this filter.
                    </p>
                  )}
                  {railScope === 'document' &&
                    groupedRailSections.map(({ block, items }) => (
                      <div
                        key={block.id}
                        className="space-y-1.5"
                        data-testid={ids.railGroup(block.id)}
                      >
                        <button
                          type="button"
                          className="sticky top-0 z-[1] flex w-full items-center gap-2 rounded-none bg-muted/70 px-2 py-1 text-left text-[11px] backdrop-blur-sm"
                          onClick={() => {
                            setActiveBlockId(block.id)
                            setRailScope('block')
                            scrollBlockIntoView(block.id)
                          }}
                        >
                          <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[10px] font-semibold text-primary">
                            {items.length}
                          </span>
                          <span className="min-w-0 flex-1 truncate capitalize text-muted-foreground">
                            <span className="font-medium text-foreground">
                              {block.kind}
                            </span>
                            {' · '}
                            {blockPreviewLabel(block)}
                          </span>
                        </button>
                        <div className="space-y-1.5">
                          {items.map((item) => renderThreadCard(item))}
                        </div>
                      </div>
                    ))}
                </div>

                {activeBlockId && draft && (
                  <div
                    className="relative shrink-0 border-t border-border p-2"
                    data-testid={ids.railComposer}
                  >
                    <div className="mb-1.5 flex min-w-0 items-center gap-1.5 px-0.5 text-[11px] text-muted-foreground">
                      <span className="shrink-0 capitalize text-foreground">
                        {activeBlock?.kind ?? 'block'}
                      </span>
                      {draft.excerpt ? (
                        <span className="inline-flex min-w-0 flex-1 items-center gap-1 truncate text-primary">
                          <Highlighter className="h-3 w-3 shrink-0" />
                          <span className="truncate">{draft.excerpt}</span>
                          <button
                            type="button"
                            className="shrink-0"
                            aria-label="Clear range"
                            onClick={() => setDraft({ ...draft, excerpt: null })}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ) : (
                        <span className="truncate">Whole block</span>
                      )}
                    </div>

                    {draft.mentions.length > 0 && (
                      <div className="mb-1.5 flex flex-wrap gap-1 px-1" data-testid={ids.mentionChips}>
                        {draft.mentions.map((m) => (
                          <button
                            key={m}
                            type="button"
                            className="inline-flex items-center gap-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary"
                            onClick={() =>
                              setDraft({
                                ...draft,
                                mentions: draft.mentions.filter((x) => x !== m),
                                text: draft.text.replace(m, '').replace(/\s+/g, ' ').trim(),
                              })
                            }
                          >
                            {m}
                            <X className="h-3 w-3 opacity-60" />
                          </button>
                        ))}
                      </div>
                    )}

                    <textarea
                      ref={composerRef}
                      className="min-h-[64px] w-full resize-none rounded-none border border-border bg-background px-2 py-1.5 text-sm outline-none focus-visible:ring-1 focus-visible:ring-primary"
                      placeholder={
                        draft.mode === 'suggest'
                          ? 'Suggest an edit - @agent optional'
                          : 'Add a comment - type @ to tag'
                      }
                      value={draft.text}
                      onChange={(e) => onComposerChange(e.target.value)}
                      data-testid={ids.railComposerInput}
                    />

                    {mentionSuggestions.length > 0 && (
                      <div
                        className="absolute bottom-[108px] left-2 right-2 z-20 overflow-hidden rounded-md border border-border bg-popover shadow-lg"
                        data-testid={ids.mentionMenu}
                      >
                        {mentionSuggestions.map((m) => (
                          <button
                            key={m.id}
                            type="button"
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-muted"
                            onClick={() => addMention(m)}
                          >
                            <AtSign className="h-3.5 w-3.5 text-muted-foreground" />
                            <span className="font-medium">{m.handle}</span>
                            <span className="text-muted-foreground">{m.label}</span>
                            <span className="ml-auto text-[10px] uppercase text-muted-foreground">
                              {m.kind}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}

                    <div className="mt-1.5 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs"
                          onClick={() =>
                            setDraft({
                              ...draft,
                              mode: draft.mode === 'suggest' ? 'comment' : 'suggest',
                            })
                          }
                          data-testid={ids.railComposerMode}
                        >
                          {draft.mode === 'suggest' ? 'Suggest edit' : 'Comment'}
                          <ChevronDown className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          className="flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground hover:text-foreground"
                          title="Insert @"
                          data-testid={ids.mentionButton}
                          onClick={() => {
                            if (!draft) return
                            setDraft({ ...draft, text: draft.text + (draft.text.endsWith(' ') || !draft.text ? '@' : ' @') })
                            setMentionQuery('')
                            composerRef.current?.focus()
                          }}
                        >
                          <AtSign className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <Button
                        type="button"
                        size="icon"
                        className="h-8 w-8 rounded-full"
                        onClick={submitCompose}
                        disabled={!draft.text.trim()}
                        data-testid={ids.railComposerSubmit}
                        aria-label="Submit"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </aside>
            )}

            {agentOpen && (
              <aside
                className="flex w-[300px] shrink-0 flex-col border-l border-border bg-muted/20"
                data-testid={ids.agentPanel}
              >
                <div className="flex h-11 items-center justify-between gap-2 border-b border-border px-3">
                  <div className="flex items-center gap-2">
                    <ChatIcon className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">Agent chat</span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => setAgentOpen(false)}
                    aria-label="Close agent panel"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div
                  className="flex-1 space-y-2 overflow-y-auto p-3 text-xs"
                  data-testid={ids.orchLog}
                >
                  {orchLog.map((line, i) => (
                    <p key={i} className="rounded-md bg-card px-2 py-1.5">
                      {line}
                    </p>
                  ))}
                </div>
                <div className="space-y-2 border-t border-border p-3">
                  <Button
                    type="button"
                    size="sm"
                    className="w-full gap-1"
                    onClick={runOrchestratorReview}
                    data-testid={ids.orchReview}
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    Review all suggestions
                  </Button>
                </div>
              </aside>
            )}
          </div>
        </main>
      </div>
    </div>
  )
}
